import React, { useContext, useEffect, useState } from "react"
import DeleteAvailabilityModal from "./DeleteAvailabilityModal"
import InfoModal from "../../InfoModal"
import MultiSelectServices, { ServiceOption } from "./MultiSelectServices"
import Select, { SingleValue } from "react-select"
import TimezoneSelect, { allTimezones } from "react-timezone-select"
import useLoading from "../../../hooks/useLoading"
import useScrollToTop from "../../../hooks/useScrollToTop"
import {
  AvailabilityRulesData,
  AvailabilityObjs,
} from "../../../types/availabilityTypes"
import { Button } from "../../ui/button"
import { CalendarRequests } from "../../../api/app.service"
import { defaultAvailability } from "../../../objects/availabilityObjects"
import { EditAvailabilitySettingsType } from "../../../models/app.interface"
import { hours, bufferTime } from "../../../objects/timeObjects"
import { Input } from "../../ui/input"
import { ServiceRequests } from "../../../api/app.service"
import {
  SvgFilledInformationCircle,
  SvgCheckbox,
  SvgVideoIcon,
  SvgUsersSolid,
  SvgCheckboxOff,
  SvgPhone,
  SvgWarningSign,
  SvgChevronLeft,
  SvgCheckCircleSolid,
  SvgCopy,
  SvgClose,
  SvgPlus,
  SvgTrash,
} from "../../icons"
import { useLocation } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import { useParams } from "react-router-dom"
import { useToast } from "../../../hooks/useToast"
import { UserCtx } from "../../../context/userContext"

const AvailabilityPage = () => {
  const { user } = useContext(UserCtx)
  const { id } = useParams<{ id: string }>()
  const { toast } = useToast()
  const { startLoading, stopLoading } = useLoading()
  const location = useLocation()
  const pathParts = location.pathname.split("/")
  const navigate = useNavigate()
  const [availability, setAvailability] = useState(defaultAvailability)
  const [isUnsavedCalendar, setIsUnsavedCalendar] = useState<boolean>(false)
  const [selectedSchedule, setSelectedSchedule] =
    useState<AvailabilityRulesData>(defaultAvailability)
  const [services, setServices] = useState<any[]>([])
  const [selectedDays, setSelectedDays] = useState<string[]>([])
  const [showBufferTime, setShowBufferTime] = useState<boolean>(false)
  const notDefault = !selectedSchedule?.is_default && id
  const [showBookingWindowDesc, setShowBookingWindowDesc] =
    useState<boolean>(false)
  const [showCancelNoticeDesc, setShowCancelNoticeDesc] =
    useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [slotErrors, setSlotErrors] = useState<Record<string, Set<number>>>({})
  const [originalSchedule, setOriginalSchedule] =
    useState<AvailabilityRulesData | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [availabilityToDelete, setAvailabilityToDelete] =
    useState<AvailabilityRulesData | null>(null)
  const [selectedServices, setSelectedServices] = useState<ServiceOption[]>([])

  const DAYS = [
    { id: "sun", label: "S", full: "Sunday" },
    { id: "mon", label: "M", full: "Monday" },
    { id: "tue", label: "T", full: "Tuesday" },
    { id: "wed", label: "W", full: "Wednesday" },
    { id: "thu", label: "T", full: "Thursday" },
    { id: "fri", label: "F", full: "Friday" },
    { id: "sat", label: "S", full: "Saturday" },
  ]

  type Days =
    | "sunday"
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday"

  const matchingServices = services.filter(
    (service) =>
      service.service_details.availability_rule_id === selectedSchedule.id
  )

  const isDefault = Boolean(selectedSchedule?.is_default)

  const toggleDay = (id: string) => {
    setSelectedSchedule((prevSchedule) => {
      const dayName = idToFullDay[id] as Days
      const isSelected = prevSchedule.weekly_periods.some(
        (wp) => wp.day === dayName
      )

      let updatedWeeklyPeriods: AvailabilityObjs[]

      if (isSelected) {
        updatedWeeklyPeriods = prevSchedule.weekly_periods.filter(
          (wp) => wp.day !== dayName
        )
      } else {
        updatedWeeklyPeriods = [
          ...prevSchedule.weekly_periods,
          {
            day: dayName,
            start_time: "09:00",
            end_time: "17:00",
          },
        ]
      }

      updatedWeeklyPeriods.sort(
        (a, b) =>
          DAYS.findIndex((d) => d.full.toLowerCase() === a.day) -
          DAYS.findIndex((d) => d.full.toLowerCase() === b.day)
      )

      const updatedSelectedDays = updatedWeeklyPeriods.map((wp) =>
        dayNameToId(wp.day)
      )

      setSelectedDays(updatedSelectedDays)
      setIsUnsavedCalendar(true)

      return {
        ...prevSchedule,
        weekly_periods: updatedWeeklyPeriods,
      }
    })
  }

  const getServices = () => {
    if (user) {
      ServiceRequests.getServiceListClient({
        profile_id: user.activeProfileId,
        profile_type: user.activeProfile,
      })
        .then((data) => {
          setServices(data.service_list)
          setLoading(false)
        })
        .catch((ex) => {
          console.error(ex)
          toast.error(ex.response.data.message)
          setLoading(false)
        })
        .finally(() => {
          stopLoading()
        })
    }
  }

  const groupedByDay = selectedSchedule.weekly_periods.reduce((acc, slot) => {
    if (!acc[slot.day]) {
      acc[slot.day] = []
    }
    acc[slot.day].push(slot)
    return acc
  }, {} as Record<string, Array<{ start_time: string; end_time: string }>>)

  const getAvailabilityRules = () => {
    if (user?.coachProfile) {
      setLoading(true)
      CalendarRequests.getAvailabilityRules({
        user_id: parseInt(user.user_id),
      })
        .then((data) => {
          if (data.availability_rules.length > 0) {
            const pathParts = location.pathname.split("/")
            const availabilityIdFromUrl = parseInt(pathParts[3], 10)

            if (availabilityIdFromUrl) {
              const scheduleToSet = data.availability_rules.find(
                (a) => a.id === availabilityIdFromUrl
              )

              if (scheduleToSet) {
                setSelectedSchedule(scheduleToSet)
                setAvailability(scheduleToSet)
              } else {
                toast.error("Selected availability not found.")
              }
            } else {
              const latestSchedule =
                data.availability_rules.at(-1) || defaultAvailability
              setSelectedSchedule(latestSchedule)
              setAvailability(latestSchedule)
              setIsUnsavedCalendar(false)
            }
          } else {
            setIsUnsavedCalendar(true)
            setSelectedSchedule(defaultAvailability)
            setAvailability(defaultAvailability)
          }
          setLoading(false)
        })
        .catch((ex) => {
          console.error(ex)
          toast.error(ex.response?.data?.message)
          setLoading(false)
        })
    }
  }

  const setAvailFxn = async () => {
    if (
      !(
        selectedSchedule.name &&
        selectedSchedule.timezone &&
        selectedSchedule.weekly_periods.length > 0 &&
        selectedSchedule.min_book_window !== undefined &&
        selectedSchedule.min_book_window !== null
      )
    ) {
      if (!selectedSchedule.name) {
        toast.error("Please add a name for this schedule before saving.")
      } else if (!selectedSchedule.timezone) {
        toast.error("Please select a timezone for this schedule before saving.")
      } else if (!(selectedSchedule.weekly_periods.length > 0)) {
        toast.error(
          "You must choose at least one day to be available before saving."
        )
      }
      return
    }

    if (!user?.coachProfile) return

    startLoading()

    const changedSettings: EditAvailabilitySettingsType["settings"] = {}
    if (selectedSchedule.buffer_before !== originalSchedule?.buffer_before) {
      changedSettings.buffer_before = selectedSchedule.buffer_before
    }
    if (selectedSchedule.buffer_after !== originalSchedule?.buffer_after) {
      changedSettings.buffer_after = selectedSchedule.buffer_after
    }
    if (selectedSchedule.status !== originalSchedule?.status) {
      changedSettings.status = selectedSchedule.status
    }
    if (
      (selectedSchedule as any).cancel_notice_period !==
      originalSchedule?.cancel_notice_period
    ) {
      changedSettings.cancel_notice_period =
        selectedSchedule.cancel_notice_period
    }
    if (
      selectedSchedule.min_book_window !== originalSchedule?.min_book_window
    ) {
      changedSettings.min_book_window = selectedSchedule.min_book_window
    }
    if (
      selectedSchedule.timezone?.value !== originalSchedule?.timezone?.value
    ) {
      changedSettings.timezone = selectedSchedule.timezone
    }

    const onlySettingsChanged =
      Object.keys(changedSettings).length > 0 &&
      selectedSchedule.weekly_periods === originalSchedule?.weekly_periods &&
      selectedSchedule.name === originalSchedule?.name

    try {
      if (onlySettingsChanged) {
        await CalendarRequests.editAvailabilitySettings({
          user_id: parseInt(user.user_id),
          availability_rule_id: selectedSchedule.id!,
          settings: changedSettings,
        })
        toast.success("Availability updated!")
      } else {
        const cleanedWeeklyPeriods = selectedSchedule.weekly_periods.filter(
          (wp) => wp.start_time && wp.end_time && wp.start_time < wp.end_time
        )

        const stripeProductIds = selectedServices
          .map((service) => service.stripe_product_id)
          .filter((id) => id) as string[]

        await CalendarRequests.setAvailabilityRules({
          user_id: parseInt(user.user_id),
          availability_rules: [
            {
              ...selectedSchedule,
              weekly_periods: cleanedWeeklyPeriods,
              stripe_products: stripeProductIds,
            },
          ],
        })

        if (selectedSchedule.id) {
          toast.success("Availability updated!")
          setIsUnsavedCalendar(false)
        } else {
          toast.success("Availability created!")
          const allAvailabilities = await CalendarRequests.getAvailabilityRules(
            {
              user_id: parseInt(user.user_id),
            }
          )
          const newAvailability = allAvailabilities.availability_rules.at(-1)

          if (newAvailability?.id) {
            setSelectedSchedule(newAvailability)
            setAvailabilityToDelete(newAvailability)
            setIsUnsavedCalendar(false)
            navigate(`/account-settings/availability/${newAvailability.id}`)
          }
        }
      }
    } catch (ex: any) {
      console.error(ex)
      toast.error(ex.response?.data?.message || "Failed to update availability")
    } finally {
      stopLoading()
      getAvailabilityRules()
    }
  }

  const handleAvailabilityNameChange = (e: any) => {
    setIsUnsavedCalendar(true)
    setSelectedSchedule({
      ...selectedSchedule,
      name: e.target.value,
    })
  }

  const handleTimeZoneChange = (tz: { value: string; label: string }) => {
    setSelectedSchedule((prev) => ({
      ...prev,
      timezone: tz,
    }))
    setIsUnsavedCalendar(true)
  }

  const handleAvailabilityBufferBeforeChange = (
    e: SingleValue<{
      value: number
      label: string
    }>
  ) => {
    setIsUnsavedCalendar(true)
    setSelectedSchedule({
      ...selectedSchedule,
      buffer_before: e?.value || 0,
    })
  }

  const handleAvailabilityBufferAfterChange = (
    e: SingleValue<{
      value: number
      label: string
    }>
  ) => {
    setIsUnsavedCalendar(true)
    setSelectedSchedule({
      ...selectedSchedule,
      buffer_after: e?.value || 0,
    })
  }

  const handleBookingWindowChange = (e: any) => {
    const value = e.target.value

    if ((value === "" || /^[0-9]*$/.test(value)) && value.length <= 3) {
      const valueInHours = parseInt(value, 10) || 0
      const valueInMinutes = valueInHours * 60

      setSelectedSchedule({
        ...selectedSchedule,
        min_book_window: valueInMinutes,
      })
      setIsUnsavedCalendar(true)
    }
  }

  const handleCancelPolicyChange = (e: any) => {
    const value = e.target.value

    if ((value === "" || /^[0-9]*$/.test(value)) && value.length <= 3) {
      const valueInHours = parseInt(value, 10) || 0
      const valueInMinutes = valueInHours * 60

      setSelectedSchedule({
        ...selectedSchedule,
        cancel_notice_period: valueInMinutes,
      })
      setIsUnsavedCalendar(true)
    }
  }

  const dayNameToId = (dayName: string) => {
    switch (dayName.toLowerCase()) {
      case "sunday":
        return "sun"
      case "monday":
        return "mon"
      case "tuesday":
        return "tue"
      case "wednesday":
        return "wed"
      case "thursday":
        return "thu"
      case "friday":
        return "fri"
      case "saturday":
        return "sat"
      default:
        return ""
    }
  }

  useEffect(() => {
    if (!user?.coachProfile) return

    const trueAvailability = location.state?.availability

    if (id) {
      startLoading()
      CalendarRequests.getAvailabilityRules({ user_id: parseInt(user.user_id) })
        .then((data) => {
          const schedule = data.availability_rules.find(
            (a) => a.id === parseInt(id)
          )
          if (schedule) {
            const sortedWeeklyPeriods = [...schedule.weekly_periods].sort(
              (a, b) =>
                DAYS.findIndex((d) => d.full.toLowerCase() === a.day) -
                DAYS.findIndex((d) => d.full.toLowerCase() === b.day)
            )
            setSelectedSchedule({
              ...schedule,
              weekly_periods: sortedWeeklyPeriods,
            })
            setOriginalSchedule({
              ...schedule,
              weekly_periods: sortedWeeklyPeriods,
            })
            setSelectedDays(
              sortedWeeklyPeriods.map((wp) => dayNameToId(wp.day))
            )
            setIsUnsavedCalendar(false)
            getServices()
          } else {
            toast.error("Schedule not found")
          }
        })
        .catch((err) => {
          console.error(err)
          toast.error("Failed to load availability")
        })
        .finally(() => {
          stopLoading()
        })
    } else {
      const newSchedule = trueAvailability || {
        ...defaultAvailability,
        timezone: user?.timezone
          ? {
              value: user.timezone.value,
              label: user.timezone.label || user.timezone.value,
            }
          : {
              value: Intl.DateTimeFormat().resolvedOptions().timeZone,
              label: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
      }

      const sortedWeeklyPeriods = newSchedule.weekly_periods
        ? [...newSchedule.weekly_periods].sort(
            (a, b) =>
              DAYS.findIndex((d) => d.full.toLowerCase() === a.day) -
              DAYS.findIndex((d) => d.full.toLowerCase() === b.day)
          )
        : []

      setSelectedSchedule({
        ...newSchedule,
        weekly_periods: sortedWeeklyPeriods,
        id: null,
      })
      setOriginalSchedule(null)
      setSelectedDays(sortedWeeklyPeriods.map((wp) => dayNameToId(wp.day)))
      setIsUnsavedCalendar(true)
      getServices()
    }
  }, [id, user, location.state])

  const idToFullDay: Record<string, Days> = {
    sun: "sunday",
    mon: "monday",
    tue: "tuesday",
    wed: "wednesday",
    thu: "thursday",
    fri: "friday",
    sat: "saturday",
  }

  const handleSlotTimeChange = (
    day: string,
    slotIndex: number,
    e: any,
    startOrEnd: "start" | "end"
  ) => {
    setSelectedSchedule((prev) => {
      const slotsForDay = prev.weekly_periods.filter((wp) => wp.day === day)

      if (!slotsForDay[slotIndex]) {
        console.warn(`No slot found for day ${day} at index ${slotIndex}`)
        return prev
      }

      const updatedSlot = {
        ...slotsForDay[slotIndex],
        [startOrEnd === "start" ? "start_time" : "end_time"]: e.value,
      }

      if (updatedSlot.start_time >= updatedSlot.end_time) {
        setSlotErrors((prevErrors) => {
          const errorsForDay = new Set(prevErrors[day] || [])
          errorsForDay.add(slotIndex)
          return {
            ...prevErrors,
            [day]: errorsForDay,
          }
        })
      } else {
        setSlotErrors((prevErrors) => {
          const errorsForDay = new Set(prevErrors[day] || [])

          if (updatedSlot.start_time >= updatedSlot.end_time) {
            errorsForDay.add(slotIndex)
          } else {
            errorsForDay.delete(slotIndex)
          }

          return {
            ...prevErrors,
            [day]: errorsForDay,
          }
        })
      }

      const newWeeklyPeriods = prev.weekly_periods.filter(
        (wp) => !(wp.day === day && wp === slotsForDay[slotIndex])
      )
      newWeeklyPeriods.push(updatedSlot)

      newWeeklyPeriods.sort((a, b) => {
        if (a.day !== b.day) {
          return (
            DAYS.findIndex((d) => d.full.toLowerCase() === a.day) -
            DAYS.findIndex((d) => d.full.toLowerCase() === b.day)
          )
        }
        return a.start_time.localeCompare(b.start_time)
      })

      return {
        ...prev,
        weekly_periods: newWeeklyPeriods,
      }
    })
    setIsUnsavedCalendar(true)
  }

  const handleAddSlot = (day: string) => {
    setSelectedSchedule((prev: any) => {
      const newSlot = { day, start_time: "09:00", end_time: "17:00" }
      const newWeeklyPeriods = [...prev.weekly_periods, newSlot]

      newWeeklyPeriods.sort((a, b) => {
        if (a.day !== b.day) {
          return (
            DAYS.findIndex((d) => d.full.toLowerCase() === a.day) -
            DAYS.findIndex((d) => d.full.toLowerCase() === b.day)
          )
        }
        return a.start_time.localeCompare(b.start_time)
      })

      return { ...prev, weekly_periods: newWeeklyPeriods }
    })
    setIsUnsavedCalendar(true)
  }

  const handleRemoveSlot = (day: string, slotIndex: number) => {
    setSelectedSchedule((prev) => {
      const slotsForDay = prev.weekly_periods.filter((wp) => wp.day === day)
      const slotToRemove = slotsForDay[slotIndex]
      const newWeeklyPeriods = prev.weekly_periods.filter(
        (wp) => wp !== slotToRemove
      )

      const hasSlotsLeft = newWeeklyPeriods.some((wp) => wp.day === day)

      setSelectedDays((prevSelectedDays) => {
        if (!hasSlotsLeft) {
          return prevSelectedDays.filter((d) => d !== dayNameToId(day))
        }
        return prevSelectedDays
      })

      return { ...prev, weekly_periods: newWeeklyPeriods }
    })
    setIsUnsavedCalendar(true)
  }

  const handleCopyTimesToAll = () => {
    setSelectedSchedule((prev) => {
      if (prev.weekly_periods.length === 0) return prev

      const sourceSlot = prev.weekly_periods[0]
      if (!sourceSlot) return prev

      const uniqueDays = Array.from(
        new Set(prev.weekly_periods.map((wp) => wp.day))
      )

      const newWeeklyPeriods = uniqueDays.map((day) => ({
        day,
        start_time: sourceSlot.start_time,
        end_time: sourceSlot.end_time,
      }))

      newWeeklyPeriods.sort(
        (a, b) =>
          DAYS.findIndex((d) => d.full.toLowerCase() === a.day) -
          DAYS.findIndex((d) => d.full.toLowerCase() === b.day)
      )

      return { ...prev, weekly_periods: newWeeklyPeriods }
    })
    setIsUnsavedCalendar(true)
  }

  const handleDeleteClick = (availability: AvailabilityRulesData) => {
    setAvailabilityToDelete(availability)
    setShowDeleteModal(true)
  }

  const deleteAvailabilityRule = () => {
    if (!availabilityToDelete?.id) {
      toast.error("You must save a newly created calendar before deleting it.")
      return
    }

    if (availabilityToDelete?.is_default) {
      toast.error("Can't delete the default availability rule.")
      return
    }

    setLoading(true)

    CalendarRequests.deleteAvailabilityRule({
      user_id: parseInt(user.user_id),
      availability_rule_id: availabilityToDelete.id,
    })
      .then(() => {
        toast.success("Availability successfully deleted")
        navigate(`/account-settings/calendars`)
      })
      .catch((ex) => {
        console.error(ex)
        toast.error(
          ex.response?.data?.message || "Failed to delete availability"
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useScrollToTop()

  return (
    <div>
      <div className="fixed top-0 w-screen h-[88px] create-service-shadow bg-white z-[401] overflow-x-hidden top-20 flex items-center px-4 md:px-32 justify-between">
        <div className="p-2">
          <Button
            variant="ghost"
            onClick={() => navigate(`/account-settings/calendars`)}
          >
            <SvgChevronLeft className="md:rounded-full md:border md:border-grayCloud h-6 w-6 md:h-8 md:w-8 md:p-1 md:group-hover:bg-grayCloud transition-colors" />
            <span className="hidden sm:inline">
              back to <span className="text-black">Calendar Settings</span>
            </span>
          </Button>
        </div>

        <div className="flex p-2 md:mr-12">
          {notDefault && (
            <Button
              variant="destructive"
              className="flex items-center px-4 py-2 text-sm whitespace-nowrap justify-start"
              onClick={() => handleDeleteClick(availability)}
            >
              <SvgTrash className="inline mr-2" /> Delete
            </Button>
          )}
          <Button
            onClick={setAvailFxn}
            className="ml-4"
            disabled={!isUnsavedCalendar}
          >
            Save
            <SvgCheckCircleSolid />
          </Button>
        </div>
      </div>
      <div className=" flex flex-col justify-center sm:px-8 md:px-12 lg:px-32 py-8">
        <div className="main-shadow bg-white rounded-xl p-16">
          <div className="text-[28px] font-bold mb-12">
            Availability Settings
          </div>
          <div className="lg:w-[50%] mb-6">
            <Input
              label="Availability Name"
              type="text"
              placeholder="Availability Name"
              onChange={handleAvailabilityNameChange}
              name="scheduleName"
              value={selectedSchedule.name || ""}
            />
          </div>
          <div className="mb-6">
            <div className="mb-6">
              {selectedSchedule?.timezone && (
                <div className="flex items-center gap-2">
                  <p className="font-bold text-lg">Timezone:</p>
                  <TimezoneSelect
                    className="lg:w-[325px] w-full basic-single"
                    classNamePrefix="select"
                    value={selectedSchedule.timezone}
                    name="timezone"
                    id="timezone"
                    onChange={(e) => handleTimeZoneChange(e)}
                    timezones={{
                      ...allTimezones,
                      [user?.timezone?.value ?? ""]:
                        user?.timezone?.value ?? "",
                    }}
                  />
                </div>
              )}
            </div>

            <div className="mb-6">
              <div className="flex flex-col lg:flex-row lg:items-center gap-2">
                <div className="flex items-center gap-2">
                  <p className="font-bold text-lg">Buffer Windows:</p>
                  <Button
                    onClick={() => setShowBufferTime(true)}
                    variant="link"
                    size="icon"
                  >
                    <SvgFilledInformationCircle className="w-4 h-4" />
                  </Button>
                </div>

                <div className="flex flex-col md:flex-row md:items-center md:gap-4 gap-2 mt-2 md:mt-0 w-full">
                  <div className="flex items-center justify-between w-full md:w-auto">
                    <span className="font-bold text-sm text-graySlate w-16 md:w-auto">
                      Before
                    </span>
                    <Select
                      className="basic-single flex-1 w-[156px]"
                      classNamePrefix="select"
                      options={bufferTime}
                      placeholder="0 minutes"
                      value={bufferTime.filter(
                        (option) =>
                          option.value === selectedSchedule.buffer_before
                      )}
                      onChange={handleAvailabilityBufferBeforeChange}
                    />
                  </div>

                  {/* After */}
                  <div className="flex items-center justify-between w-full md:w-auto">
                    <span className="font-bold text-sm text-graySlate w-16 md:w-auto">
                      After
                    </span>
                    <Select
                      className="basic-single flex-1 w-[156px]"
                      classNamePrefix="select"
                      options={bufferTime}
                      placeholder="0 minutes"
                      value={bufferTime.filter(
                        (option) =>
                          option.value === selectedSchedule.buffer_after
                      )}
                      onChange={handleAvailabilityBufferAfterChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-2">
                <p className="font-bold text-lg">Minimum Booking Notice: </p>
                <Button
                  onClick={() => setShowBookingWindowDesc(true)}
                  variant="link"
                  size="icon"
                >
                  <SvgFilledInformationCircle className="w-4 h-4" />
                </Button>
                <Input
                  label="Hours"
                  type="text"
                  onChange={handleBookingWindowChange}
                  name="bookingWindow"
                  placeholder="0"
                  value={selectedSchedule.min_book_window / 60 || "0"}
                  className="text-left w-[90px] appearance-none pr-8"
                  style={{ textAlign: "center" }}
                />
              </div>
            </div>

            <div className="cursor-not-allowed">
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                <div className="flex items-center gap-2 sm:order-1">
                  <p className="font-bold text-lg">Cancellation Notice:</p>
                  <Button
                    onClick={() => setShowCancelNoticeDesc(true)}
                    variant="link"
                    size="icon"
                  >
                    <SvgFilledInformationCircle className="w-4 h-4" />
                  </Button>
                  <Input
                    label="Hours"
                    type="text"
                    onChange={handleCancelPolicyChange}
                    name="CancelPolicy"
                    placeholder="0"
                    value={selectedSchedule.cancel_notice_period / 60 || "0"}
                    className="text-left w-[90px] appearance-none pr-8"
                    style={{ textAlign: "center" }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-grayMist w-full h-[1px] mt-12 mb-12"></div>
          <div className="">
            <p className="text-2xl font-bold mb-4">Connected Services</p>
            <p className="text-lg mb-6">
              Select the service this availability applies to. Clients booking
              this service will only see the times you’ve set here.
            </p>
            <MultiSelectServices
              options={services}
              selected={selectedServices}
              onChange={setSelectedServices}
              availability={selectedSchedule}
              matchingServices={matchingServices}
              setIsUnsavedCalendar={setIsUnsavedCalendar}
            />
            {isDefault ? (
              <p className="mt-4 italic text-gray-700">
                <strong>This availability is set as the default.</strong>{" "}
                Services originally connected to the Default Availability cannot
                be manually removed. To remove a service from the default,
                simply assign it to another availability.
              </p>
            ) : (
              <p className="mt-4 italic text-gray-700">
                Any services originally attached to this availability that are
                removed will automatically be attached to the default
                availability.
              </p>
            )}
          </div>
          <div className="bg-grayMist w-full h-[1px] mt-12 mb-12"></div>
          <div className="">
            <p className="text-2xl font-bold mb-4">Working Hours</p>
            <p className="text-lg mb-8">
              Set your working hours so others know when you’re available for
              meetings.
            </p>
            <div className="flex flex-wrap gap-4 mb-8">
              {DAYS.map(({ id, label }) => {
                const isSelected = selectedDays.includes(id)
                return (
                  <button
                    key={id}
                    onClick={() => toggleDay(id)}
                    className="w-10 h-10 flex items-center justify-center rounded-full font-semibold text-lg"
                    style={{
                      backgroundColor: isSelected ? "#4750F5" : "#ABB8C3",
                      color: isSelected ? "#FFFFFF" : "#4750F5",
                      transition: "background-color 0.3s, color 0.3s",
                    }}
                  >
                    {label}
                  </button>
                )
              })}
            </div>

            <div className="mt-4 flex flex-col gap-6">
              {Object.entries(groupedByDay).map(([day, slots]) => {
                const dayId = dayNameToId(day)
                const dayLabel = DAYS.find((d) => d.id === dayId)?.full || day

                return (
                  <div key={dayId} className="flex flex-col gap-2">
                    {slots.map((slot, idx) => (
                      <div key={idx} className="flex flex-col w-full gap-2">
                        <div className="flex flex-col sm:flex-row items-start gap-2 w-full">
                          {" "}
                          {idx === 0 ? (
                            <span className="text-lg font-medium w-full sm:w-[100px] flex items-start  justify-start sm:justify-center pt-4 ">
                              {dayLabel}
                            </span>
                          ) : (
                            <span className="w-[100px] invisible">
                              Placeholder
                            </span>
                          )}
                          <div className="flex flex-col md:flex-row md:items-center gap-2 flex-1">
                            <div className="flex-1">
                              <Select
                                className="basic-single w-full"
                                classNamePrefix="select"
                                options={hours}
                                value={hours.filter(
                                  (o) => o.value === slot.start_time
                                )}
                                onChange={(e) =>
                                  handleSlotTimeChange(day, idx, e, "start")
                                }
                                name="startTime"
                                styles={{
                                  control: (base) => ({ ...base, height: 38 }),
                                }}
                              />
                            </div>

                            <div className="flex items-center gap-1 flex-1">
                              <span className="text-gray text-[16px] hidden md:inline">
                                -
                              </span>
                              <Select
                                className="basic-single w-full"
                                classNamePrefix="select"
                                options={hours}
                                value={hours.filter(
                                  (o) => o.value === slot.end_time
                                )}
                                onChange={(e) =>
                                  handleSlotTimeChange(day, idx, e, "end")
                                }
                                name="endTime"
                                styles={{
                                  control: (base) => ({ ...base, height: 38 }),
                                }}
                              />
                            </div>

                            <div className="flex flex-row gap-2 mt-2 md:mt-0">
                              <Button
                                variant="ghost"
                                className={`p-2 md:inline-flex ${
                                  idx !== slots.length - 1 ? "invisible" : ""
                                } hidden md:flex`}
                                onClick={() => handleAddSlot(day)}
                                aria-label="Add time slot"
                              >
                                <SvgPlus />
                              </Button>
                              <Button
                                variant="destructive"
                                className="p-2 inline-flex md:hidden"
                                onClick={() => handleRemoveSlot(day, idx)}
                                aria-label="Remove time slot"
                              >
                                Delete
                              </Button>
                              <Button
                                variant="outline"
                                className={`p-2 md:hidden ${
                                  idx !== slots.length - 1 ? "invisible" : ""
                                }`}
                                onClick={() => handleAddSlot(day)}
                                aria-label="Add time slot"
                              >
                                Add Time
                              </Button>
                              <Button
                                variant="ghost"
                                className="p-2 hidden md:inline-flex"
                                onClick={() => handleRemoveSlot(day, idx)}
                                aria-label="Remove time slot"
                              >
                                <SvgClose />
                              </Button>
                              <Button
                                variant="ghost"
                                className={`flex items-center space-x-1 ${
                                  idx === 0 &&
                                  dayId ===
                                    dayNameToId(
                                      selectedSchedule.weekly_periods[0].day
                                    )
                                    ? ""
                                    : "invisible"
                                }`}
                                aria-label="Copy times to all days"
                                onClick={handleCopyTimesToAll}
                              >
                                <SvgCopy style={{ color: "#4750F5" }} />
                                <span className="text-[#4750F5] hidden xl:inline">
                                  Copy times to all
                                </span>
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          </div>{" "}
          <div className="bg-grayMist w-full h-[1px] mt-12 mb-12"></div>
          <div className="mb-6">
            <p className="text-2xl font-bold mb-4">Session Format</p>
            <p className="text-lg mb-8">
              Pick the way you want to meet! Clients will see this so they know
              whether to join online, call, or meet in person.
            </p>
            <div className="flex mb-2 items-center">
              <SvgCheckbox className="mr-4" />
              <SvgVideoIcon className="mr-2" /> Zoee Video Call
            </div>
            <div className="flex mb-2 items-center text-[#ABB8C3] cursor-not-allowed">
              <SvgCheckboxOff className="mr-4" fill="#ABB8C3" />
              <SvgUsersSolid
                className="mr-2"
                style={{ color: "#ABB8C3" }}
              />{" "}
              In-Person{" "}
              <span className="ml-3 bg-grayMist font-semibold px-2 py-1 rounded flex items-center space-x-1 text-black">
                <SvgWarningSign />
                <span>Coming Soon</span>
              </span>{" "}
            </div>
            <div className="flex mb-2 items-center text-[#ABB8C3] cursor-not-allowed">
              <SvgCheckboxOff className="mr-4" fill="#ABB8C3" />
              <SvgPhone className="mr-2" /> Phone Call
              <span className="ml-3 bg-grayMist font-semibold px-2 py-1 rounded flex items-center space-x-1 text-black">
                <SvgWarningSign />
                <span>Coming Soon</span>
              </span>{" "}
            </div>
          </div>
          {showBufferTime && (
            <InfoModal
              title="Buffer Windows"
              description="Add dedicated buffer times, both before and after appointments, to allow for preparation, transition, and follow-up between sessions."
              setShowModal={setShowBufferTime}
            />
          )}
          {showBookingWindowDesc && (
            <InfoModal
              title="Minimum Booking Notice"
              description="Set how much advance warning you need before sessions. With Minimum
            Booking Notice, you control how close to a time slot clients can
            book with you—whether that's 24 hours, 2 days, or just 30 minutes
            before. Prevent last-minute surprises and give yourself the
            preparation time you deserve. Perfect for maintaining your workflow
            while still offering flexibility to your clients."
              setShowModal={setShowBookingWindowDesc}
            />
          )}
          {showCancelNoticeDesc && (
            <InfoModal
              title="Cancellation Notice Policy"
              description="This setting determines how much advance notice a client must give before canceling a session. For example, if your cancellation notice period is 24 hours, clients must cancel at least 1 day before the session to avoid any penalties or disruptions to your schedule."
              setShowModal={setShowCancelNoticeDesc}
            />
          )}
          {showDeleteModal && availabilityToDelete && (
            <DeleteAvailabilityModal
              availability={availabilityToDelete}
              setShowDeleteDialogueModal={setShowDeleteModal}
              deleteAvailabilityRule={deleteAvailabilityRule}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default AvailabilityPage
